import { useModules } from '@contexts/modules-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { ErrorPage } from '@shared/components/error-pages/ErrorPage';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { Permission } from './api/fetch-permissions/fetchPermissions.types';

import { fetchPermissions } from './api/fetch-permissions/fetchPermissions';
import {
  PermissionsContext,
  type PermissionsContextValue,
  type PermissionsProviderProps,
} from './PermissionsProvider.types';

const PermissionsProvider = React.memo(
  ({ children }: Readonly<PermissionsProviderProps>): React.ReactElement => {
    const { selectedModule, modules, isModulesLoading, isModulesError } =
      useModules();
    const { selectedOrganization, isSyncingOrganization } = useOrganizations();

    const shouldFetchPermissions = React.useMemo(() => {

      if (isModulesLoading || isModulesError) return false;

      if (selectedModule?.name === 'Manage') {
        const hasValidOrg = Boolean(selectedOrganization?.id);
        return hasValidOrg && !isSyncingOrganization;
      }

      if (selectedModule) {
        return true;
      }

      if (modules?.length === 0) {
        return true;
      }

      return false;
    }, [
      selectedModule,
      selectedOrganization,
      modules,
      isModulesLoading,
      isModulesError,
      isSyncingOrganization,
    ]);

    const {
      data: permissions = [],
      isLoading: isQueryLoading,
      isError,
      error,
    } = useQuery<Permission[]>({
      queryKey: ['permissions', selectedModule?.name, selectedOrganization?.id],
      queryFn: () => fetchPermissions(selectedOrganization?.id),
      enabled: shouldFetchPermissions,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

    // In React Query v5, isLoading = isPending && isFetching.
    // When enabled:false (prerequisites not ready), isFetching=false → isLoading=false,
    // even though permissions haven't been fetched yet.
    // Report loading=true until prerequisites are ready OR modules have permanently errored.
    const isLoading =
      isQueryLoading || (!shouldFetchPermissions && !isModulesError);

    const hasPermission = React.useCallback(
      (permissionName: string): boolean => {
        if (!permissions || permissions.length === 0) {
          return false;
        }

        const hasPermission = permissions.some(p => p.name === permissionName);

        return hasPermission;
      },
      [permissions]
    );

    const value: PermissionsContextValue = React.useMemo(
      () => ({
        permissions,
        isLoading,
        isError,
        error: error,
        hasPermission,
      }),
      [permissions, isLoading, isError, error, hasPermission]
    );

    return (
      <PermissionsContext value={value}>
        {isError ? <ErrorPage type="internal-server-error" /> : children}
      </PermissionsContext>
    );
  }
);

PermissionsProvider.displayName = 'PermissionsProvider';

export { PermissionsProvider };
